import { useState, useEffect } from 'react';
import { Plus, X, AlertCircle, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FieldIssue } from '../../types';
import type { ValidationFeedback } from '@/api/campaigns';
import {
  CharCount,
  FieldLabel,
  Field,
  InlineIssue,
  AssetSlot,
  inputClass,
  issueBorder,
} from './shared';
import type { UploadRatio } from './shared';
import type { Campaign } from '@/api/campaigns';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY_TEXT_LIMIT = 80;
const HEADLINE_LIMIT = 40;
const DESCRIPTION_LIMIT = 25;
const MAX_PRIMARY_TEXTS = 6;

const CTA_OPTIONS = [
  'Apply Now',
  'Book Now',
  'Contact Us',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Order Now',
  'Shop Now',
  'Sign Up',
];

// ─── Main component ───────────────────────────────────────────────────────────

interface MetaSingleDetailsInitialValues {
  postCopies?: string[];
  headline?: string;
  description?: string;
  cta?: string;
  url?: string;
  legal?: string;
  existingImageUrl1x1?: string;
  existingImageUrl4x5?: string;
  existingImageUrl9x16?: string;
  imageContentType?: string | null; // content type of the 1:1 asset
  imageContentType4x5?: string | null; // content type of the 4:5 asset (may differ from 1:1)
  imageContentType9x16?: string | null; // content type of the 9:16 asset (may differ from 1:1)
}

interface MetaSingleDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  file1x1: File | null;
  file4x5: File | null;
  file9x16: File | null;
  onFile1x1: (f: File | null) => void;
  onFile4x5: (f: File | null) => void;
  onFile9x16: (f: File | null) => void;
  /** Which of the two feed slots (1:1 / 4:5) the shared feed mockup is showing */
  activeFeedSlot?: '1x1' | '4x5';
  onFeedSlotSelect?: (slot: '1x1' | '4x5') => void;
  /** Called when the user confirms deletion of an existing API image — parent uses this to mark dirty and queue a backend delete */
  onExistingImageClear?: (ratio: UploadRatio) => void;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onCaptionChange: (v: string) => void;
  onHeadlineChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onCtaChange: (v: string) => void;
  /** All primary text variations — used by parent to build the save payload */
  onPostCopiesChange: (copies: string[]) => void;
  /** Destination URL — used by parent to build the save payload */
  onUrlChange: (url: string) => void;
  /** Legal text — used by parent to build the save payload */
  onLegalChange: (legal: string) => void;
  initialValues?: MetaSingleDetailsInitialValues;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function MetaSingleDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  file1x1,
  file4x5,
  file9x16,
  onFile1x1: setFile1x1,
  onFile4x5: setFile4x5,
  onFile9x16: setFile9x16,
  activeFeedSlot,
  onFeedSlotSelect,
  onExistingImageClear,
  feedback,
  feedbackPending,
  onAIFeedback,
  onCaptionChange,
  onHeadlineChange,
  onDescriptionChange,
  onCtaChange,
  onPostCopiesChange,
  onUrlChange,
  onLegalChange,
  initialValues,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: MetaSingleDetailsProps) {
  const [primaryTexts, setPrimaryTexts] = useState(() =>
    initialValues?.postCopies?.length ? initialValues.postCopies : [''],
  );
  const [activePrimary, setActivePrimary] = useState(0);
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  // Track if the user has cleared an existing API image (so we can show the upload zone instead)
  const [cleared1x1, setCleared1x1] = useState(false);
  const [cleared4x5, setCleared4x5] = useState(false);
  const [cleared9x16, setCleared9x16] = useState(false);
  // When non-null, the confirmation modal is shown for that ratio before the delete is committed
  const [pendingClear, setPendingClear] = useState<UploadRatio | null>(null);
  const [headline, setHeadline] = useState(initialValues?.headline ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [cta, setCta] = useState(initialValues?.cta ?? 'Learn More');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);

  // Sync initial values into mockup and parent save state on mount
  useEffect(() => {
    onCaptionChange(primaryTexts[0] ?? '');
    onHeadlineChange(headline);
    onDescriptionChange(description);
    onCtaChange(cta);
    onPostCopiesChange(primaryTexts);
    onUrlChange(destinationUrl);
    onLegalChange(legal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasVariations = primaryTexts.length > 1;

  function addVariation() {
    if (primaryTexts.length >= MAX_PRIMARY_TEXTS) return;
    const next = [...primaryTexts, ''];
    setPrimaryTexts(next);
    setActivePrimary(next.length - 1);
    onPostCopiesChange(next);
  }

  function removeVariation(index: number) {
    const next = primaryTexts.filter((_, i) => i !== index);
    setPrimaryTexts(next);
    setActivePrimary((prev) =>
      prev >= next.length ? next.length - 1 : prev === index ? Math.max(0, index - 1) : prev,
    );
    onPostCopiesChange(next);
  }

  function updatePrimaryText(index: number, value: string) {
    const updated = [...primaryTexts];
    updated[index] = value;
    setPrimaryTexts(updated);
    if (index === activePrimary) onCaptionChange(value);
    onPostCopiesChange(updated);
  }

  function switchActivePrimary(index: number) {
    setActivePrimary(index);
    onCaptionChange(primaryTexts[index]);
  }

  // Returns the visible dominant severity for a primary-text variation tab.
  function getTabIssueType(index: number): 'error' | 'warning' | undefined {
    const issue = fieldIssues[`primary_text.${index}`];
    if (!issue) return undefined;
    if (issue.errors.length > 0 && showErrors) return 'error';
    if (issue.warnings.length > 0 && showWarnings) return 'warning';
    return undefined;
  }

  return (
    <>
      {/* Confirmation modal — shown before permanently deleting a saved image */}
      {pendingClear !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete Image
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove the{' '}
                <span className="font-semibold text-slate-800">{pendingClear}</span> image? You will
                still need to press Save to apply this change.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setPendingClear(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Exhaustive map, not an if/else chain: a new UploadRatio must
                    // be a compile error here rather than silently clearing 9:16.
                    const setCleared: Record<UploadRatio, (v: boolean) => void> = {
                      '1x1': setCleared1x1,
                      '4x5': setCleared4x5,
                      '9x16': setCleared9x16,
                    };
                    setCleared[pendingClear](true);
                    onExistingImageClear?.(pendingClear);
                    setPendingClear(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
        {/* ── About ───────────────────────────────────────────────────── */}
        <div className="border-l-2 border-[#0014cc]/30 pl-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            About this format
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            A single image or video ad that runs across Facebook and Instagram — feed, stories, and
            reels. You upload a <span className="font-medium text-slate-700">1:1</span> or{' '}
            <span className="font-medium text-slate-700">4:5</span> feed asset — ideally both — plus
            a <span className="font-medium text-slate-700">9:16</span> asset, and Meta automatically
            serves each to the right placement. You can also add up to 6 primary text variations and
            Meta will test them, serving whichever performs best.{' '}
            <a
              href="https://www.facebook.com/business/ads-guide/update/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
            >
              Meta ads guide ↗
            </a>
          </p>
        </div>

        {/* ── Gear Exchange Import ──────────────────────────────────── */}
        {
          <>
            <GearExchangeImportButton
              totalAssets={gxData?.total_assets ?? null}
              loading={gxLoading}
              error={gxError}
              fordshareIds={fordshareIds ?? undefined}
              onClick={() => setShowGxModal(true)}
            />

            {showGxModal && gxData && campaign && campaignId && (
              <GearExchangeModal
                data={gxData}
                campaign={campaign}
                campaignId={campaignId}
                formatLabel="Meta Single Image"
                zoneLimits={[
                  {
                    ratio: '1x1',
                    limit: 1,
                    existing:
                      file1x1 || (initialValues?.existingImageUrl1x1 && !cleared1x1) ? 1 : 0,
                  },
                  {
                    ratio: '4x5',
                    limit: 1,
                    existing:
                      file4x5 || (initialValues?.existingImageUrl4x5 && !cleared4x5) ? 1 : 0,
                  },
                  {
                    ratio: '9x16',
                    limit: 1,
                    existing:
                      file9x16 || (initialValues?.existingImageUrl9x16 && !cleared9x16) ? 1 : 0,
                  },
                ]}
                onClose={() => setShowGxModal(false)}
                usedInVariants={usedInVariants}
                onImport={(files: GXImportedFile[]) => {
                  const f1x1 = files.find((f) => f.ratio === '1x1');
                  const f4x5 = files.find((f) => f.ratio === '4x5');
                  const f9x16 = files.find((f) => f.ratio === '9x16');
                  if (f1x1) {
                    setFile1x1(f1x1.file);
                    setCleared1x1(true);
                  }
                  if (f4x5) {
                    setFile4x5(f4x5.file);
                    setCleared4x5(true);
                  }
                  if (f9x16) {
                    setFile9x16(f9x16.file);
                    setCleared9x16(true);
                  }
                }}
              />
            )}
          </>
        }

        {/* ── Assets ──────────────────────────────────────────────────── */}
        <div>
          <div className="grid grid-cols-3 gap-6">
            {/* The 1:1 and 4:5 slots share one feed mockup, so they form a radio
                group: clicking one chooses which is previewed. 9:16 has its own
                mockup tab and is therefore not part of the group. */}
            <AssetSlot
              label="1:1 Asset"
              tooltip="Feed placement for Facebook and Instagram. Provide a 1:1 or a 4:5 (both is best). 4:5 files are still accepted here — assets uploaded before 4:5 had its own slot live in this one."
              hint="Click to upload 1:1 image or video"
              ratio="1x1"
              fieldKey={['ratio_items.1x1', 'ratio_items.feed']}
              file={file1x1}
              onFile={setFile1x1}
              existingUrl={cleared1x1 ? undefined : initialValues?.existingImageUrl1x1}
              existingContentType={initialValues?.imageContentType}
              onClearExisting={() => setPendingClear('1x1')}
              isActive={activeFeedSlot === '1x1'}
              onSelect={() => onFeedSlotSelect?.('1x1')}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />

            <AssetSlot
              label="4:5 Asset"
              tooltip="Taller feed ratio for Facebook and Instagram. Provide a 1:1 or a 4:5 (both is best). Shares the same feed mockup as 1:1."
              hint="Click to upload 4:5 image or video"
              ratio="4x5"
              fieldKey={['ratio_items.4x5', 'ratio_items.feed']}
              file={file4x5}
              onFile={setFile4x5}
              existingUrl={cleared4x5 ? undefined : initialValues?.existingImageUrl4x5}
              existingContentType={
                initialValues?.imageContentType4x5 ?? initialValues?.imageContentType
              }
              onClearExisting={() => setPendingClear('4x5')}
              isActive={activeFeedSlot === '4x5'}
              onSelect={() => onFeedSlotSelect?.('4x5')}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />

            <AssetSlot
              label="9:16 Asset"
              tooltip="Used for Stories and Reels placements. Recommended but not required."
              hint="Click to upload 9:16 image or video"
              ratio="9x16"
              fieldKey="ratio_items.9x16"
              file={file9x16}
              onFile={setFile9x16}
              existingUrl={cleared9x16 ? undefined : initialValues?.existingImageUrl9x16}
              existingContentType={
                initialValues?.imageContentType9x16 ?? initialValues?.imageContentType
              }
              onClearExisting={() => setPendingClear('9x16')}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </div>
        </div>

        {/* ── Copy ────────────────────────────────────────────────────── */}
        <div className="space-y-5">
          {/* Primary Text */}
          <div>
            {/* Label row — single mode shows "+ Add variation" */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 cursor-default">
                      Primary Text
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-64">
                    The main copy that appears above your ad. Keep it concise and
                    attention-grabbing. You can add up to 6 variations — Meta will automatically
                    test them and serve whichever performs best.
                  </TooltipContent>
                </Tooltip>
                {!hasVariations && primaryTexts.length < MAX_PRIMARY_TEXTS && (
                  <button
                    type="button"
                    onClick={addVariation}
                    className="flex items-center gap-0.5 text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <Plus className="w-2.5 h-2.5" />
                    Add variation
                  </button>
                )}
              </div>
              <CharCount value={primaryTexts[activePrimary]} limit={PRIMARY_TEXT_LIMIT} />
            </div>

            {/* Tab strip — only shown when 2+ variations exist */}
            {hasVariations && (
              <div className="flex items-center border-b border-slate-200 mb-3">
                {primaryTexts.map((_, i) => {
                  const issueType = getTabIssueType(i);
                  return (
                    <div key={i} className="relative group/vtab">
                      <button
                        onClick={() => switchActivePrimary(i)}
                        className={`px-3 py-1.5 text-xs flex items-center gap-1 border-b-2 -mb-px transition-all ${
                          activePrimary === i
                            ? issueType === 'error'
                              ? 'text-red-600 font-semibold border-red-500'
                              : issueType === 'warning'
                                ? 'text-amber-600 font-semibold border-amber-400'
                                : 'text-[#0014cc] font-semibold border-[#0014cc]'
                            : 'text-slate-500 font-medium border-transparent hover:text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Variation {i + 1}
                        {issueType === 'error' && (
                          <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                        )}
                        {issueType === 'warning' && (
                          <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                        )}
                      </button>
                      <button
                        onClick={() => removeVariation(i)}
                        className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-slate-300 hover:bg-red-400 text-white flex items-center justify-center opacity-0 group-hover/vtab:opacity-100 transition-opacity"
                      >
                        <X className="w-2 h-2" />
                      </button>
                    </div>
                  );
                })}
                {primaryTexts.length < MAX_PRIMARY_TEXTS && (
                  <button
                    onClick={addVariation}
                    className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-[#0014cc] border-b-2 border-transparent -mb-px transition-all flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                )}
              </div>
            )}

            {/* Active textarea */}
            <textarea
              value={primaryTexts[activePrimary]}
              onChange={(e) => updatePrimaryText(activePrimary, e.target.value)}
              placeholder="Write your primary ad copy..."
              rows={3}
              className={`${inputClass} resize-none ${issueBorder(fieldIssues[`primary_text.${activePrimary}`], showErrors, showWarnings)}`}
            />
            <div className="flex items-center mt-0.5 gap-2">
              <InlineIssue
                issue={fieldIssues[`primary_text.${activePrimary}`]}
                showErrors={showErrors}
                showWarnings={showWarnings}
                feedback={feedback}
                feedbackPending={feedbackPending}
                onFeedback={onAIFeedback}
                inline
              />
            </div>
          </div>

          {/* 2×2 grid: Description, Headline, URL, CTA */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            {/* Description */}
            <Field
              label="Description"
              right={<CharCount value={description} limit={DESCRIPTION_LIMIT} />}
              tooltip="Optional supporting line that appears beneath the headline. Not always shown by Meta — keep it brief and treat it as supplementary."
              fieldKey="description"
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            >
              <input
                type="text"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  onDescriptionChange(e.target.value);
                }}
                placeholder="Short supporting description..."
                className={`${inputClass} ${issueBorder(fieldIssues['description'], showErrors, showWarnings)}`}
              />
            </Field>

            {/* Headline */}
            <Field
              label="Headline"
              right={<CharCount value={headline} limit={HEADLINE_LIMIT} />}
              tooltip="Appears below the image in the link preview section. Keep it short and direct — 40 characters max."
              fieldKey="headline"
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            >
              <input
                type="text"
                value={headline}
                onChange={(e) => {
                  setHeadline(e.target.value);
                  onHeadlineChange(e.target.value);
                }}
                placeholder="Short, punchy headline..."
                className={`${inputClass} ${issueBorder(fieldIssues['headline'], showErrors, showWarnings)}`}
              />
            </Field>

            {/* Destination URL */}
            <Field
              label="Destination URL"
              tooltip="The URL users are taken to when they click the ad. Make sure it matches the offer or message in your creative."
              fieldKey="destination_url"
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            >
              <input
                type="url"
                value={destinationUrl}
                onChange={(e) => {
                  setDestinationUrl(e.target.value);
                  onUrlChange(e.target.value);
                }}
                placeholder="https://www.ford.com/..."
                className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
              />
            </Field>

            {/* Call to Action */}
            <div>
              <FieldLabel
                label="Call to Action"
                tooltip="The button label shown on the ad. Choose the option that best matches the action you want users to take."
              />
              <select
                value={cta}
                onChange={(e) => {
                  setCta(e.target.value);
                  onCtaChange(e.target.value);
                }}
                className={`${inputClass} cursor-pointer`}
              >
                {CTA_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Legal */}
          <Field
            label="Legal"
            tooltip="Required in some markets (e.g. France) for regulated categories. Add any mandatory disclaimer copy here — it will not appear in the mockup but will be submitted with the ad."
            fieldKey="legal"
            fieldIssues={fieldIssues}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
            help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
          >
            <textarea
              value={legal}
              onChange={(e) => {
                setLegal(e.target.value);
                onLegalChange(e.target.value);
              }}
              placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
              rows={5}
              className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
            />
          </Field>
        </div>
      </div>
    </>
  );
}
